import { Box } from '@mui/material';
import { forwardRef } from 'react';

/**
 * @typedef {Object} CardProps
 * @property {React.ReactNode} children - Card content
 * @property {string} [variant] - Card variant (default, form)
 * @property {number} [alternateIndex] - Index for alternating corner patterns
 * @property {string} [color] - Accent color for borders
 * @property {string} [backgroundColor] - Background color
 * @property {number} [cornerSize] - Size of corner borders as percentage
 * @property {number} [borderWidth] - Width of corner borders
 * @property {number} [borderRadius] - Radius for rounded corners
 * @property {boolean} [hover] - Enable hover effects
 * @property {Object} [sx] - Additional MUI sx props
 * @property {Function} [onClick] - Click handler
 */

/**
 * Reusable Card component with RSM Collective signature corner treatment
 *
 * Features:
 * - Alternating corner patterns (Pattern A: top-left/bottom-right, Pattern B: top-right/bottom-left)
 * - Customizable corner size and border width
 * - Hover effects
 * - Single source of truth for card styling
 *
 * @param {CardProps} props - Component props
 * @param {React.Ref} ref - Forwarded ref
 * @returns {JSX.Element} Card component
 */
const Card = forwardRef(
  (
    {
      children,
      variant = 'default',
      alternateIndex = 0,
      color = 'var(--color-primary)',
      backgroundColor = 'var(--color-card)',
      cornerSize = 30,
      borderWidth = 4,
      borderRadius = 12,
      hover = true,
      sx = {},
      onClick,
      ...props
    },
    ref
  ) => {
    // Determine corner pattern based on alternateIndex
    const isPatternA = alternateIndex % 2 === 0;

    // Variant-specific styles
    const variantStyles = {
      default: {
        p: 3,
        minHeight: '160px',
      },
      form: {
        p: 4,
      },
      ebook: {
        p: 4,
        height: '280px',
      },
      approach: {
        p: 4,
        height: '100%',
      },
    };

    // Calculate border radius based on pattern
    const formRadius = borderRadius * 2;
    const calculatedBorderRadius =
      variant === 'form'
        ? isPatternA
          ? `0 ${formRadius}px 0 ${formRadius}px`
          : `${formRadius}px 0 ${formRadius}px 0`
        : isPatternA
          ? `0 ${borderRadius}px 0 ${borderRadius}px`
          : `${borderRadius}px 0 ${borderRadius}px 0`;

    return (
      <Box
        ref={ref}
        onClick={onClick}
        sx={{
          backgroundColor,
          borderRadius: calculatedBorderRadius || '0 12px 0 12px', // Fallback
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s ease',
          border: 'none',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          cursor: onClick ? 'pointer' : 'default',
          position: 'relative',
          overflow: 'hidden',
          containerType: 'inline-size',
          // Base styles from variant
          ...(variantStyles[variant] || variantStyles.default),
          // Responsive fallback
          '@media (max-width: 500px)': {
            p: variant === 'form' ? 3 : 2,
            minHeight: variant === 'ebook' ? '260px' : '140px',
          },
          // Container queries for modern browsers
          '@container (max-width: 250px)': {
            p: variant === 'form' ? 3 : 2,
            minHeight: variant === 'ebook' ? '260px' : '140px',
          },
          '@container (min-width: 350px)': {
            p: variant === 'form' ? 4 : 3,
            minHeight: variant === 'ebook' ? '300px' : '180px',
          },
          // Top corner (left or right based on pattern)
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: isPatternA ? 0 : 'auto',
            right: isPatternA ? 'auto' : 0,
            width: `${cornerSize}%`,
            height: `${cornerSize}%`,
            borderTop: `${borderWidth}px solid ${color}`,
            borderLeft: isPatternA ? `${borderWidth}px solid ${color}` : 'none',
            borderRight: isPatternA
              ? 'none'
              : `${borderWidth}px solid ${color}`,
          },
          // Bottom corner (right or left based on pattern)
          '&::after': {
            content: '""',
            display: 'block',
            position: 'absolute',
            bottom: 0,
            left: isPatternA ? 'auto' : 0,
            right: isPatternA ? 0 : 'auto',
            width: `${cornerSize}%`,
            height: `${cornerSize}%`,
            borderBottom: `${borderWidth}px solid ${color}`,
            borderLeft: isPatternA ? 'none' : `${borderWidth}px solid ${color}`,
            borderRight: isPatternA
              ? `${borderWidth}px solid ${color}`
              : 'none',
          },
          // Hover effects
          ...(hover && {
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 30px ${color}40`,
              backgroundColor:
                variant === 'default' ? `${color}05` : backgroundColor,
            },
          }),
          // Additional sx props override
          ...sx,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }
);

Card.displayName = 'Card';

export default Card;
